import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import {
  NumberInputControl,
  NumberInputField,
  NumberInputRoot,
} from '@/components/ui/number-input';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import type { createListCollection } from '@chakra-ui/react';
import { Button, FormatNumber, HStack, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { CampaignBudgetNotice } from './CampaignBudgetNotice';
import { BOOST_SETUP_FIELD_HEIGHT } from './useMentionBoostSetupForm';

interface BudgetControlsProps {
  budgetAmountInCents: number;
  budgetCurrencyCode: string;
  budgetCurrencyDisplay: string;
  budgetWholeUnitInvalid: boolean;
  budgetPresets: number[];
  durationCollection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  durationDays: number;
  durationPresets: number[];
  onBudgetChange: (details: { valueAsNumber: number }) => void;
  onDurationChange: (value: string[]) => void;
  /**
   * The chosen campaign holds its budget centrally (CBO). The daily amount then
   * belongs to the campaign, not to the boost: the input, the presets and the
   * total summary make no sense and are replaced by the explanation card. The
   * campaign name it is set on drives that copy.
   */
  campaignBudgetName?: string | null;
  /**
   * The stored campaign is still being resolved: neither control is correct yet,
   * so hold the space instead of flashing the manual amount and replacing it.
   */
  isResolvingCampaignBudget?: boolean;
}

export function BudgetControls({
  budgetAmountInCents,
  budgetCurrencyCode,
  budgetCurrencyDisplay,
  budgetWholeUnitInvalid,
  budgetPresets,
  durationCollection,
  durationDays,
  onBudgetChange,
  onDurationChange,
  campaignBudgetName = null,
  isResolvingCampaignBudget = false,
}: BudgetControlsProps) {
  const { t } = useTranslation();

  if (isResolvingCampaignBudget) {
    return (
      <>
        <Skeleton h={BOOST_SETUP_FIELD_HEIGHT} rounded="md" />
        <Field label={t('mentions.boost.setup.durationLabel')}>
          <DurationSelect
            durationCollection={durationCollection}
            durationDays={durationDays}
            onDurationChange={onDurationChange}
            maxW="160px"
          />
        </Field>
      </>
    );
  }

  if (campaignBudgetName !== null) {
    return (
      <>
        <CampaignBudgetNotice campaignName={campaignBudgetName} />
        <Field label={t('mentions.boost.setup.durationLabel')}>
          <DurationSelect
            durationCollection={durationCollection}
            durationDays={durationDays}
            onDurationChange={onDurationChange}
            maxW="160px"
          />
        </Field>
      </>
    );
  }

  return (
    <>
      <SimpleGrid columns={2} gap={3}>
        <Field label={t('mentions.boost.setup.budgetTotalLabel')}>
          <NumberInputRoot
            min={1}
            step={1}
            value={(budgetAmountInCents / 100).toString()}
            onValueChange={onBudgetChange}
            colorPalette="gray"
            size="sm"
          >
            <NumberInputControl h={BOOST_SETUP_FIELD_HEIGHT} />
            <InputGroup
              startElement={
                <Text fontSize="sm" fontWeight="medium" color="gray.500">
                  {budgetCurrencyDisplay}
                </Text>
              }
              startOffset="4px"
            >
              <NumberInputField h={BOOST_SETUP_FIELD_HEIGHT} fontWeight="medium" pe={9} />
            </InputGroup>
          </NumberInputRoot>
        </Field>
        <Field label={t('mentions.boost.setup.durationLabel')}>
          <DurationSelect
            durationCollection={durationCollection}
            durationDays={durationDays}
            onDurationChange={onDurationChange}
          />
        </Field>
      </SimpleGrid>

      <PresetRow
        values={budgetPresets}
        selectedValue={budgetAmountInCents}
        formatValue={(value) =>
          new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: budgetCurrencyCode,
            maximumFractionDigits: 0,
          }).format(value / 100)
        }
        onSelect={(value) => onBudgetChange({ valueAsNumber: value / 100 })}
      />

      <BudgetSummary
        budgetAmountInCents={budgetAmountInCents}
        currencyCode={budgetCurrencyCode}
        durationDays={durationDays}
      />
      {budgetWholeUnitInvalid && (
        <Text fontSize="xs" color="red.600">
          <Trans i18nKey="mentions.boost.setup.budgetWholeUnitRequired" />
        </Text>
      )}
    </>
  );
}

interface DurationSelectProps {
  durationCollection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  durationDays: number;
  onDurationChange: (value: string[]) => void;
  maxW?: string;
}

/** Shared duration picker: the only budget control left under a CBO campaign. */
function DurationSelect({
  durationCollection,
  durationDays,
  onDurationChange,
  maxW,
}: DurationSelectProps) {
  return (
    <SelectRoot
      collection={durationCollection}
      value={[String(durationDays)]}
      onValueChange={(details) => onDurationChange(details.value)}
      maxW={maxW}
    >
      <SelectTrigger
        h={BOOST_SETUP_FIELD_HEIGHT}
        css={{
          '& [data-part="trigger"]': {
            height: BOOST_SETUP_FIELD_HEIGHT,
          },
        }}
      >
        <SelectValueText />
      </SelectTrigger>
      <SelectContent>
        {durationCollection.items.map((item) => (
          <SelectItem key={item.value} item={item}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
}

interface PresetRowProps {
  formatValue: (value: number) => string;
  onSelect: (value: number) => void;
  selectedValue: number;
  values: number[];
}

function PresetRow({ formatValue, onSelect, selectedValue, values }: PresetRowProps) {
  return (
    <HStack gap={1.5} flexWrap="wrap">
      {values.map((value) => (
        <Button
          key={value}
          size="xs"
          h={8}
          px={3}
          rounded="md"
          variant={selectedValue === value ? 'solid' : 'outline'}
          colorPalette={selectedValue === value ? 'primary' : 'gray'}
          onClick={() => onSelect(value)}
        >
          {formatValue(value)}
        </Button>
      ))}
    </HStack>
  );
}

interface BudgetSummaryProps {
  budgetAmountInCents: number;
  currencyCode: string;
  durationDays: number;
}

function BudgetSummary({ budgetAmountInCents, currencyCode, durationDays }: BudgetSummaryProps) {
  const currencyProps = {
    currency: currencyCode,
    style: 'currency' as const,
  };

  return (
    <Text fontSize="xs" color="gray.500">
      <Trans
        i18nKey="mentions.boost.setup.budgetSummary"
        values={{ duration: durationDays }}
        components={{
          totalBudget: <FormatNumber value={budgetAmountInCents / 100} {...currencyProps} />,
        }}
      />
    </Text>
  );
}
